import React, { useState, useEffect } from 'react';
import logoImg from '../../assets/logo.png';
import clsx from 'clsx';
import type { AccordionItem } from '@/components/SidebarAccordion/SidebarAccordion';

export interface SidebarProps {
  children?: React.ReactNode;
  logo?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  menuItems?: AccordionItem[];
}

export const Sidebar = ({
  children,
  description,
  logo,
  className,
  menuItems,
}: SidebarProps) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    // Sprawdź rozmiar ekranu przy inicjalizacji
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1440;
    }
    return false;
  });

  // Wykrywanie rozmiaru ekranu
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1440);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Blokowanie scrollowania body gdy menu otwarte na mobile
  useEffect(() => {
    if (isMobileOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen, isMobile]);

  // Zamykanie menu po kliknięciu w overlay
  const handleOverlayClick = () => {
    setIsMobileOpen(false);
  };

  // Zamykanie menu po naciśnięciu Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMobileOpen]);

  return (
    <>
      {/* Overlay - tylko na mobile gdy menu otwarte */}
      {isMobile && isMobileOpen && (
        <div
          className={clsx(
            'fixed inset-0 z-40 bg-black transition-opacity duration-300',
            isMobileOpen ? 'opacity-50' : 'opacity-0',
          )}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          // Desktop styles - zawsze widoczny
          'fixed bottom-0 left-0 top-0 z-50 w-64 bg-primary-200 shadow-lg',
          // Mobile styles - slide-in od lewej
          'desktop:translate-x-0 desktop:transition-none',
          {
            // Mobile: ukryty domyślnie, widoczny gdy otwarty
            '-translate-x-full transition-transform duration-300 ease-in-out':
              isMobile && !isMobileOpen,
            'translate-x-0 transition-transform duration-300 ease-in-out':
              isMobile && isMobileOpen,
            // Mobile: szerokość 60%
            'w-[60%]': isMobile,
          },
          className,
        )}
      >
        <div className="relative h-full">
          {/* Przycisk zamknięcia - tylko na mobile gdy sidebar otwarty */}
          {isMobile && isMobileOpen && (
            <button
              onClick={() => setIsMobileOpen(false)}
              className={clsx(
                'absolute right-[-20px] top-4 z-10 flex items-center justify-center',
                'h-10 w-10 rounded-full bg-[#20262A]',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              )}
              aria-label="Zamknij menu"
            >
              <span className="mb-1 text-[30px] font-bold leading-none text-white">
                &times;
              </span>
            </button>
          )}

          <div className="">
            <h2 className="px-4 pt-2 pb-4 text-xl font-bold border-b border-gray-200">
              {logo ? (
                logo
              ) : (
                <img src={logoImg} alt="Logo" className="w-full" />
              )}
            </h2>
            {description && (
              <div className="px-4 pt-4 pb-4 mb-4 bg-white border-b border-primary-100">
                {description}
              </div>
            )}

            {/* Menu items - dynamically generated from report sections */}
            {menuItems && menuItems.length > 0 && (
              <nav className="px-4 py-2">
                <ul className="space-y-1">
                  {menuItems.map((item) => (
                    <li key={item.value}>
                      <a
                        href={`#${item.elementId || item.value}`}
                        onClick={() => {
                          if (isMobile) {
                            setIsMobileOpen(false);
                          }
                        }}
                        className={clsx(
                          'block rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          'hover:bg-primary-100 hover:text-primary-800',
                          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                        )}
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            {/* Custom children fallback */}
            {children}
          </div>
        </div>
      </div>

      {/* Przycisk otwarcia menu - tylko na mobile gdy menu zamknięte */}
      {isMobile && !isMobileOpen && (
        <button
          onClick={() => setIsMobileOpen(true)}
          className={clsx(
            'flex fixed right-6 bottom-6 justify-center items-center z-[9999]',
            'bg-white rounded-full shadow-2xl h-[80px] w-[80px] p-[10px]',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          )}
          aria-label="Otwórz menu"
          style={{ zIndex: 9999 }}
        >
          <div className="flex justify-center items-center w-full h-full rounded-full bg-gradient-primary">
            <svg
              width="28"
              height="24"
              viewBox="0 0 28 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M25.7895 19.7647C26.3572 19.765 26.903 19.9745 27.3138 20.3498C27.7247 20.725 27.9691 21.2374 27.9964 21.7806C28.0236 22.3238 27.8317 22.8562 27.4604 23.2676C27.089 23.6789 26.5667 23.9376 26.0017 23.9901L25.7895 24H2.21053C1.64284 23.9997 1.09702 23.7902 0.686167 23.415C0.275316 23.0397 0.0309296 22.5273 0.00364846 21.9841C-0.0236327 21.4409 0.168283 20.9085 0.539627 20.4971C0.910971 20.0858 1.43328 19.8271 1.99832 19.7746L2.21053 19.7647H25.7895ZM25.7895 9.88235C26.3757 9.88235 26.938 10.1055 27.3526 10.5026C27.7671 10.8997 28 11.4384 28 12C28 12.5616 27.7671 13.1003 27.3526 13.4974C26.938 13.8945 26.3757 14.1176 25.7895 14.1176H2.21053C1.62426 14.1176 1.062 13.8945 0.647448 13.4974C0.232894 13.1003 0 12.5616 0 12C0 11.4384 0.232894 10.8997 0.647448 10.5026C1.062 10.1055 1.62426 9.88235 2.21053 9.88235H25.7895ZM25.7895 0C26.3757 0 26.938 0.223109 27.3526 0.620245C27.7671 1.01738 28 1.55601 28 2.11765C28 2.67928 27.7671 3.21791 27.3526 3.61505C26.938 4.01219 26.3757 4.23529 25.7895 4.23529H2.21053C1.62426 4.23529 1.062 4.01219 0.647448 3.61505C0.232894 3.21791 0 2.67928 0 2.11765C0 1.55601 0.232894 1.01738 0.647448 0.620245C1.062 0.223109 1.62426 0 2.21053 0H25.7895Z"
                fill="white"
              />
            </svg>
          </div>
        </button>
      )}
    </>
  );
};

export default Sidebar;
